import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePedigreeInstance } from '../../../pedigree-tool/pedigree/instanceContext';
import { ThemeContext } from 'src/store/ContextStore';
import {
  clearDropTarget,
  highlightDropTarget
} from '../../../pedigree-tool/dropTargetHighlight';
import { showLegendDragChip } from './legendDragChip';
import {
  DROP_REFUSAL,
  chartSvgIn,
  findDropCandidate,
  resolveDropMember
} from './resolveDropTarget';

// Dragging a legend entry onto a person — the one implementation behind the
// Diseases, Phenotypes and Genotypes legends, which used to carry ~200 lines of
// this each, copied and drifting.
//
// What changed, and why:
// - The drop is resolved on the `drop` event, whose coordinates every engine
//   reports reliably, instead of at `dragend` from the last `drag` position
//   (Safari's dragend coordinates are unreliable, and Chrome fires a final
//   `drag` at 0,0 that the old code stored).
// - Only the target symbol is highlighted, and it is put back exactly as it was
//   on every way out — dropped, missed, Esc, unmount. See dropTargetHighlight.js.
// - `dragenter` / `dragover` are only accepted over this chart's own SVG — and
//   not where the family card covers it — so the cursor says "no drop"
//   everywhere else and a drop elsewhere writes nothing.

// The repaint touches only the DOM, and finish() cancels a pending frame, so it
// can never run against an unmounted legend. Without rAF it simply runs now.
const requestFrame = (cb) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    return window.requestAnimationFrame(cb);
  }
  cb();
  return null;
};
const cancelFrame = (id) => {
  if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
    window.cancelAnimationFrame(id);
  }
};

/**
 * @param {object} opts
 * @param {boolean} opts.enabled  rows can be dragged at all
 * @param {object[]} opts.memberRecords  the live member records
 * @param {(item: object) => object} opts.chipFor  the chip's label and colours
 * @param {(item: object, target: object) => boolean|void} opts.onDrop
 *   Called once per drop on the chart with `{ member, canvasPid }` or
 *   `{ reason }`. Return true to keep the target highlighted after the drag
 *   ends (while a dialog about that person is open); call `releaseHighlight`
 *   when done.
 */
export const useLegendNodeDrop = ({
  enabled,
  memberRecords,
  chipFor,
  onDrop
}) => {
  const { containerRef } = usePedigreeInstance();
  const { theme } = useContext(ThemeContext);
  const [draggedId, setDraggedId] = useState(null);
  const dragRef = useRef(null);

  // Handlers attached for the life of one drag read these, never a stale
  // render's props.
  const latest = useRef({});
  latest.current = { memberRecords, onDrop, accent: theme?.accent };

  const rootEl = () => containerRef?.current || null;

  const paint = (drag, pid) => {
    if (pid === drag.paintedPid) return;
    drag.paintedPid = pid;
    if (pid == null) clearDropTarget(rootEl());
    else highlightDropTarget(rootEl(), pid, latest.current.accent);
  };

  const targetAt = (point) => {
    const candidate = point ? findDropCandidate(rootEl(), point) : null;
    return candidate
      ? resolveDropMember(candidate.datum, latest.current.memberRecords)
      : { reason: DROP_REFUSAL.outOfRange };
  };

  const finish = (keepHighlight = false) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    document.removeEventListener('dragenter', drag.onDragEnter);
    document.removeEventListener('dragover', drag.onDragOver);
    document.removeEventListener('drop', drag.onDrop);
    if (drag.frame != null) cancelFrame(drag.frame);
    drag.removeChip?.();
    if (!keepHighlight) clearDropTarget(rootEl());
    setDraggedId(null);
  };

  const onDragStart = (event, item) => {
    if (!enabled) return;
    finish();

    const drag = {
      item,
      point: null,
      frame: null,
      paintedPid: null,
      hold: false,
      // The family card the legend sits in. It is drawn opaque but is
      // click-through (pointer-events: none) so clicks reach the chart, which
      // means a pointer over its padding, its gaps or the dates strip hit-tests
      // as the SVG BEHIND it — and a release there wrote the record to a person
      // hidden under the card. Anywhere inside its box is not the chart.
      shield: event.currentTarget?.closest?.('[data-legend-drop-shield]')
    };

    const overShield = (ev) => {
      const r = drag.shield?.getBoundingClientRect?.();
      return Boolean(
        r &&
          r.width &&
          ev.clientX >= r.left &&
          ev.clientX <= r.right &&
          ev.clientY >= r.top &&
          ev.clientY <= r.bottom
      );
    };

    const overChart = (ev) => {
      const svg = chartSvgIn(rootEl());
      return Boolean(
        svg &&
          ev.target instanceof Node &&
          svg.contains(ev.target) &&
          !overShield(ev)
      );
    };

    // WebKit fires `drop` only if the drag's LAST update was accepted, and an
    // update that moves the pointer onto a new element dispatches `dragenter`
    // alone. So `dragenter` has to be accepted exactly as `dragover` is, or a
    // release just after crossing onto a symbol silently does nothing in Safari.
    drag.onDragEnter = (ev) => {
      if (!overChart(ev)) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
    };

    drag.onDragOver = (ev) => {
      if (!overChart(ev)) {
        drag.point = null;
      } else {
        // Accepting the drop is what makes the browser fire `drop` here.
        ev.preventDefault();
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
        drag.point = { x: ev.clientX, y: ev.clientY };
      }
      // dragover fires every few ms; resolve and repaint once per frame.
      if (!drag.framePending) {
        drag.framePending = true;
        drag.frame = requestFrame(() => {
          drag.framePending = false;
          drag.frame = null;
          if (dragRef.current !== drag) return;
          const target = drag.point ? targetAt(drag.point) : null;
          paint(drag, target?.member ? target.canvasPid : null);
        });
      }
    };

    drag.onDrop = (ev) => {
      if (!overChart(ev)) return;
      ev.preventDefault();
      const target = targetAt({ x: ev.clientX, y: ev.clientY });
      paint(drag, target.member ? target.canvasPid : null);
      drag.hold = Boolean(latest.current.onDrop?.(drag.item, target));
    };

    try {
      event.dataTransfer.effectAllowed = 'copy';
      // Firefox will not start a drag that carries no data.
      event.dataTransfer.setData('text/plain', item?.name || item?.id || '');
    } catch {
      /* a synthetic event without a DataTransfer — nothing to set */
    }

    document.addEventListener('dragenter', drag.onDragEnter);
    document.addEventListener('dragover', drag.onDragOver);
    document.addEventListener('drop', drag.onDrop);
    drag.removeChip = showLegendDragChip(event, chipFor(item));
    dragRef.current = drag;
    setDraggedId(item?.id ?? null);
  };

  const onDragEnd = () => finish(Boolean(dragRef.current?.hold));

  const releaseHighlight = useCallback(
    () => clearDropTarget(containerRef?.current || null),
    [containerRef]
  );

  // Unmounted mid-drag (tab closed, legend hidden): leave nothing behind.
  useEffect(
    () => () => finish(),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const rowDragProps = (item) => ({
    draggable: Boolean(enabled),
    onDragStart: (event) => onDragStart(event, item),
    onDragEnd
  });

  return { draggedId, rowDragProps, releaseHighlight };
};
